/**
 * Patient List by Practitioner Page — 患者リスト(施術師毎) / /visits/visitpatient_init.
 * Common screen between A2 (はり・きゅう師) and R2 (マッサージ師). Labels branch on
 * useTheme().serviceType; data source branches in the BE via X-Service-Type header.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppButton } from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import BottomBar from '@/components/organisms/Footer';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import {
  useVisitPatientSearch,
  useVisitPatientMassagers,
  useVisitPatientInsurerTypes,
} from '@/hooks/main/useVisitPatient';
import { usePrintVisitPatientMutation } from '@/hooks/main/usePrintVisitPatientMutation';
import { useExportVisitPatientCsvMutation } from '@/hooks/main/useExportVisitPatientCsvMutation';
import VisitPatientSearchForm from './components/VisitPatientSearchForm';
import type { VisitPatientSearchFormValues } from './components/VisitPatientSearchForm';
import VisitPatientTable from './components/VisitPatientTable';
import type { VisitPatientSearchParams, PrintVisitPatientPayload } from './types';
import {
  formValuesToSearchParams,
  searchParamsToFormValues,
  formValuesToSearchPayload,
} from './urlParams';

function buildPayload(values: VisitPatientSearchFormValues): PrintVisitPatientPayload {
  return formValuesToSearchPayload(values);
}

const VisitPatientPage: React.FC = () => {
  // useClinicContext() throws if no auth context — keep the call as a guard.
  useClinicContext();
  const { serviceType } = useTheme();
  const sidebarOpen = useSidebarOpen();

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Hydrate form defaults from URL once on mount. `null` = use built-in defaults.
  const initialFormValues = useMemo(
    () => searchParamsToFormValues(urlSearchParams),
    // Intentional: run once on first render. Subsequent navigations re-mount the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [searchParams, setSearchParams] = useState<VisitPatientSearchParams | null>(() => {
    if (!initialFormValues) return null;
    return { ...buildPayload(initialFormValues), page: 1, per_page: 50 };
  });
  const [massagerTargetDate, setMassagerTargetDate] = useState<string | null>(
    () => searchParams?.visit_date_from ?? null
  );
  const hasSearched = searchParams !== null;

  const { massagers } = useVisitPatientMassagers(massagerTargetDate);
  const { insurerTypes } = useVisitPatientInsurerTypes();
  const { rows, pagination, isLoading } = useVisitPatientSearch(searchParams);
  const { triggerPrint, isLoading: isPrinting } = usePrintVisitPatientMutation();
  const { triggerExport, isLoading: isExporting } = useExportVisitPatientCsvMutation();

  const handleSearch = useCallback(
    (values: VisitPatientSearchFormValues) => {
      const payload = buildPayload(values);
      const params: VisitPatientSearchParams = { ...payload, page: 1, per_page: 50 };
      setSearchParams(params);
      setUrlSearchParams(formValuesToSearchParams(values, params), { replace: true });
    },
    [setUrlSearchParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (!searchParams) return;
      setSearchParams(prev => (prev ? { ...prev, page } : null));
    },
    [searchParams]
  );

  // Print/CSV reuse the active search filters; strip pagination — exports cover
  // the full result set per spec §6.1/§6.2.
  const exportPayload = useMemo<PrintVisitPatientPayload | null>(() => {
    if (!searchParams) return null;
    const { page: _page, per_page: _perPage, ...rest } = searchParams;
    return rest;
  }, [searchParams]);

  const handlePrint = useCallback(() => {
    if (!exportPayload) return;
    triggerPrint(exportPayload);
  }, [exportPayload, triggerPrint]);

  const handleExportCsv = useCallback(() => {
    if (!exportPayload) return;
    triggerExport(exportPayload);
  }, [exportPayload, triggerExport]);

  const totalCount = pagination?.total ?? 0;
  const hasResults = hasSearched && rows.length > 0;

  return (
    <div className="pb-4">
      <VisitPatientSearchForm
        onSearch={handleSearch}
        onDateFromChange={setMassagerTargetDate}
        massagers={massagers}
        insurerTypes={insurerTypes}
        serviceType={serviceType}
        isLoading={isLoading}
        initialValues={initialFormValues}
        totalCount={totalCount}
      />

      {hasSearched ? (
        <CollapsibleCard>
          <VisitPatientTable
            data={rows}
            pagination={pagination}
            onPageChange={handlePageChange}
            isLoading={isLoading}
            serviceType={serviceType}
          />
        </CollapsibleCard>
      ) : null}

      {/* Legacy layout: 一覧印刷 + CSV出力 left-aligned at bottom (A2/R2
          schedule_visit_patient.blade.php:330-331). 一覧印刷 = secondary style,
          CSV出力 = primary. Mount/unmount the bar to dodge the Rules-of-Hooks
          bug in shared Footer.tsx when toggling `visible`. */}
      {hasResults ? (
        <BottomBar
          variant="action"
          fixed
          className={sidebarOpen ? 'left-60' : 'left-0'}
          left={
            <div className="flex gap-3">
              <AppButton
                type="default"
                className="rounded"
                loading={isPrinting}
                onClick={handlePrint}
              >
                一覧印刷
              </AppButton>
              <AppButton
                type="primary"
                className="rounded"
                loading={isExporting}
                onClick={handleExportCsv}
              >
                CSV出力
              </AppButton>
            </div>
          }
        />
      ) : null}
    </div>
  );
};

export default VisitPatientPage;
